/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import store from '@/store';
import * as api from '@/api/user';
import { ProfileOptionsData, GameOption } from '@/api/types/user';
import { USER, COMMON } from '@/store/types';
import { StoreDataCache, findCache, saveCache } from '@/store/cache';
import { finalizeAction, StoreActionEnv, ActionType } from '@/store/actions';
import { createPayment } from '@/utils/create-payment';

export interface StoreUserProfileState {
  options: ProfileOptionsData;
  gameOptions: StoreDataCache<GameOption>[];
}

export interface StoreUserProfileGetters {
  options: ProfileOptionsData;
}

export default {
  namespaced: true,
  state: {
    options: null,
    gameOptions: [],
  },
  getters: {
    options: state => state.options || [],
  },
  actions: {
    [USER.GET_PROFILE_OPTIONS](
      { commit, state }: StoreActionEnv<StoreUserProfileState>,
      { action: rawAction, silent }: { action: ActionType; silent: boolean } = { action: '', silent: false },
    ) {
      const action = finalizeAction(rawAction, state.options);
      if (action) {
        return new Promise<void>((resolve, reject) => {
          api.getProfileOptions(silent).then((res) => {
            commit(USER.GET_PROFILE_OPTIONS, res.data);
            resolve();
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [USER.GET_GAME_OPTIONS](
      { commit, state }: StoreActionEnv<StoreUserProfileState>,
      { action: rawAction = '', id, scene }: { action: ActionType; id: string; scene: string },
    ) {
      const key = `${scene}/${id}`;
      const action = finalizeAction(rawAction, findCache(state.gameOptions, key));
      if (action) {
        return new Promise<void>((resolve, reject) => {
          api.getGameOptions(id, scene).then((res) => {
            commit(USER.GET_GAME_OPTIONS_SUCCESS, {
              id: key,
              options: res.data,
            });
            resolve();
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [USER.SET_PROFILE](_, body) {
      return new Promise((resolve, reject) => {
        createPayment(() => api.setProfile(body, { errcodeExpected: [445, 446] })).then(() => {
          if (body.isTodaySigned || body.weekSigned) {
            store.dispatch(`common/start/${COMMON.GET_START}`, { action: 'refresh' });
            store.dispatch(`user/access/${USER.GET_ACCESS}`);
          }
          if (body.vipType || body.vipDuration) {
            store.dispatch(`user/vip/${USER.GET_VIP}`);
          }
          if (body.id) {
            store.dispatch(`user/other/${USER.GET_OTHER}`, {
              action: 'reload',
              id: body.id,
              full: body.coverPic ? 'Y' : '',
            });
          }
          store.dispatch(`user/${USER.GET}`, {
            action: 'refresh',
            silent: true,
          }).then(resolve);
        }).catch(reject);
      });
    },
  },
  mutations: {
    [USER.GET_PROFILE_OPTIONS](state, data: ProfileOptionsData) {
      state.options = data;
    },
    [USER.GET_GAME_OPTIONS_SUCCESS](state: StoreUserProfileState, { id, options }: { id: string; options: GameOption }) {
      saveCache(state.gameOptions, id, options);
    },
  },
};
